import { useTranslation } from "react-i18next";
import { useNavTranslation } from "@/hooks/useNavTranslation";
import { createElement, useMemo } from "react";

import { useGetSidebarPersmissionsQuery } from "@/services/(my-space)/userApi";
import type { ISidebarLink } from "@/types/(company)/roles.interface";
import { NavIcon } from "@/components/layout/sidebar/nav-icon";
import { getSidebarDisplayName, sortSidebarLinks } from "@/utils/sidebar";

import { MODULE_KEYWORDS, SUBPAGE_GROUPS } from "./global-search.data";
import { guidesRootFor } from "@/components/guides/guide-utils";
import { GUIDES_ICON } from "@/components/guides/guide-constants";
import { matchesFilter } from "./types";
import type {
  ISearchEntry,
  SearchFilter,
  SearchFilterId,
  SearchSpace,
} from "./types";

export { matchesFilter };
export type { ISearchEntry, SearchFilter, SearchFilterId, SearchSpace };

/** `nav:tabs.*` lookup keys — the space chips render through `t`, not raw. */
const SPACE_LABEL_KEY: Record<SearchSpace, string> = {
  myspace: "nav:tabs.my_space",
  company: "nav:tabs.workspace",
};

/**
 * Builds the global-search index from the user's *permission-filtered* sidebar
 * links — so results can only ever include modules the role can open. Each
 * accessible module then pulls in its statically-defined sub-pages (tabs, "new"
 * forms) from SUBPAGE_GROUPS, gated by the module's own access.
 */
export const useGlobalSearchIndex = () => {
  // Module names come from the permissions API and sub-page labels from a static
  // registry — both are English, so they go through the same `nav:` lookup the
  // sidebar uses rather than rendering raw.
  const { translateModule, translateTab } = useNavTranslation();
  const { t } = useTranslation(["nav", "common"]);
  const { data: sidebarLinks, isLoading } = useGetSidebarPersmissionsQuery();

  const entries = useMemo<ISearchEntry[]>(() => {
    const buildSpace = (
      links: ISidebarLink[] | undefined,
      space: SearchSpace,
    ): ISearchEntry[] => {
      const sorted = sortSidebarLinks(links ?? [], space);
      const out: ISearchEntry[] = [];

      for (const link of sorted) {
        // Same url/icon derivation the sidebar uses, so paths stay in sync.
        const url = `/${link.key}`;
        const label = translateModule(
          link.key,
          getSidebarDisplayName(link, space),
        );
        const icon = createElement(NavIcon, { src: link.icon });

        out.push({
          path: url,
          label,
          module: label,
          isModule: true,
          space,
          icon,
          keywords: MODULE_KEYWORDS[url] ?? [],
        });

        // Attach this module's sub-pages — only because the module is accessible.
        const group = SUBPAGE_GROUPS.find((g) => g.parentRoot === url);
        if (!group) continue;
        for (const sub of group.items) {
          out.push({
            path: sub.path,
            label: translateTab(sub.label),
            module: label,
            parentLabel: sub.parentLabel
              ? translateTab(sub.parentLabel)
              : undefined,
            isModule: false,
            space,
            icon,
            keywords: [...(sub.keywords ?? []), label],
          });
        }
      }

      // Guides is in every menu but isn't a permission section (see
      // useSidebarNavigation), so it is added here the same way.
      if (out.length) {
        const label = translateModule("guides", "Guides");
        out.push({
          path: guidesRootFor(space),
          label,
          module: label,
          isModule: true,
          space,
          icon: createElement(NavIcon, { src: GUIDES_ICON }),
          keywords: ["help", "tutorial", "how to", "video", "learn"],
        });
      }

      return out;
    };

    return [
      ...buildSpace(sidebarLinks?.data?.individual, "myspace"),
      ...buildSpace(sidebarLinks?.data?.company, "company"),
    ];
  }, [sidebarLinks, translateModule, translateTab]);

  /** Entries grouped by space, in sidebar order, for sectioned rendering. */
  const groups = useMemo(
    () =>
      (["myspace", "company"] as SearchSpace[])
        .map((space) => ({
          space,
          label: t(SPACE_LABEL_KEY[space]),
          entries: entries.filter((e) => e.space === space),
        }))
        .filter((g) => g.entries.length > 0),
    [entries],
  );

  /**
   * Selectable scope chips ("tags"), derived from the index — no manual sync.
   * Order: All → space chips (only when both spaces exist) → one chip per
   * distinct accessible module, in sidebar order (deduped by label so a module
   * present in both spaces yields a single chip that matches either space).
   */
  const filters = useMemo<SearchFilter[]>(() => {
    const out: SearchFilter[] = [
      { id: "all", label: t("nav:tabs.all"), kind: "all" },
    ];

    const spaces = new Set(entries.map((e) => e.space));
    if (spaces.has("myspace") && spaces.has("company")) {
      out.push({
        id: "space:myspace",
        label: t(SPACE_LABEL_KEY.myspace),
        kind: "space",
      });
      out.push({
        id: "space:company",
        label: t(SPACE_LABEL_KEY.company),
        kind: "space",
      });
    }

    const seenModule = new Set<string>();
    for (const e of entries) {
      if (!e.isModule || seenModule.has(e.module)) continue;
      seenModule.add(e.module);
      out.push({
        id: `module:${e.module}`,
        label: e.module,
        kind: "module",
        icon: e.icon,
      });
    }

    return out;
  }, [entries]);

  return { entries, groups, filters, isLoading };
};
